<template>
  <div class="active-flutctuation">
    <PmwAccordion v-if="!hideChartSection" class="pmw-accordion">
      <PmwAccordionItem
        :startOpen="true"
        title="Ver gráfico de regras ativas"
        description=""
        ref="accordion-1"
        class="pmw-accordion--item"
      >
        <template v-slot:content>
          <PmwHeading
            class="active-flutctuation--title"
            size="heading-20"
            weight="weight-bold"
          >
            Gráfico de regras ativas

            <PmwInfoTooltip class="title--tooltip" width="200px">
              <template #content>
                O gráfico mostra apenas as suas
                <strong>flutuações ativas</strong>.
                <br />
                <br />
                Regras sinalizadas com <strong>*</strong> são regras
                <strong>não-públicas</strong>
              </template>
            </PmwInfoTooltip>
          </PmwHeading>

          <div class="graph">
            <ColumnRange
              :hasLegend="false"
              :xAxis="xAxis"
              :yAxis="yAxis"
              :seriesData="series"
              :tooltipFormat="tooltipFormat"
              :customHeight="customHeight"
              id="column-range"
            />
          </div>
        </template>
      </PmwAccordionItem>
    </PmwAccordion>
  </div>
</template>

<script>
import {
  areIntervalsOverlapping,
  min,
  max,
  eachDayOfInterval,
  differenceInDays,
  format,
  add
} from 'date-fns'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwInfoTooltip from '@solucoes/components-lib-tmp/src/components/patterns/PmwInfoTooltip'
import PmwAccordion from '@solucoes/components-lib-tmp/src/components/patterns/PmwAccordion'
import PmwAccordionItem from '@solucoes/components-lib-tmp/src/components/patterns/PmwAccordion/PmwAccordionItem'
import ColumnRange from '@/components/graphs/ColumnRange.vue'

export default {
  name: 'ActiveFluctuationRates',

  components: {
    ColumnRange,
    PmwAccordion,
    PmwAccordionItem,
    PmwHeading,
    PmwInfoTooltip
  },

  props: {
    reportData: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      customHeight: '',

      xAxis: {
        gridLineWidth: 1,
        tickWidth: 1,
        tickLength: 195,
        tickColor: '#e6e6e6',
        lineColor: '#e6e6e6',
        colorByPoint: true,
        endOnTick: true,
        categories: [],
        labels: {
          align: 'left',
          distance: 190,
          step: 1,

          formatter: function() {
            const maxWidth = 24
            let label = this.value.nome

            if (label.length > maxWidth) {
              label = label.substring(0, maxWidth - 3) + '...'
            }

            if (this.value.prioritaria) {
              return `${label} <br> <small style="font-size: 11px; color: #888; font-weight: 300;">• prioritária</small>`
            }

            return label
          },

          style: {
            fontSize: 14,
            color: '#474747'
          }
        }
      },

      yAxis: [
        {
          gridLineWidth: 1,
          tickWidth: 1,
          tickLength: 30,
          tickColor: '#e6e6e6',
          lineColor: '#e6e6e6',
          endOnTick: false,
          maxPadding: 0,
          type: 'datetime',
          colorByPoint: true,

          title: {
            text: ''
          }
        }
      ],

      series: [
        {
          name: '',
          data: []
        }
      ],

      tooltipFormat: {
        formatter: function() {
          return `
            <b>${this.x.nome}</b><br />
            <b>${format(this.point.low, 'dd/MM/yyyy')}</b> - <b>${format(
            this.point.high,
            'dd/MM/yyyy'
          )}</b>
          `
        }
      },

      /**
       * Armazena as datas de hospedagem das tarifas prioritárias
       * para usar de blackout nas tarifas NÃO prioritárias
       */
      priorityHostingDatesRates: [],

      /** Esconde a seção do gráfico */
      hideChartSection: false
    }
  },

  watch: {
    async reportData(newVal) {
      await this.setData(newVal)
    }
  },

  methods: {
    setData(data) {
      this.series[0].data = []
      this.xAxis.categories = []
      const randomColors = this.createRandomColorArray(data.length)

      /** Inicialize as datas mínimas e máximas com valores extremos */
      let minDate = Number.MAX_VALUE
      let maxDate = Number.MIN_VALUE

      /** Se tem menos que 2 tarifas não mostra o gráfico */
      if (data.length < 2) {
        this.hideChartSection = true
        return false
      }

      data.forEach((item, index) => {
        /**
         * Se não é prioriária, adiciona as datas de hospedagem
         * priotitárias como datas de blackout
         */
        if (!item.prioritaria) {
          item.datasBlackout = [
            ...item.datasBlackout,
            ...this.priorityHostingDatesRates
          ]
        }

        this.xAxis.categories.push(
          item.publica === false
            ? { nome: `${item.nome} *`, prioritaria: item.prioritaria }
            : { nome: item.nome, prioritaria: item.prioritaria }
        )

        /** Para armazenar os períodos de hospedagem após remover o blackout */
        let periods = []

        if (item.datasHospedagem.length && !item.datasBlackout.length) {
          item.datasHospedagem.forEach((data) => {
            periods.push({
              dataInicio: this.fixDateLocaleTime(data.dataInicio),
              dataFim: this.fixDateLocaleTime(data.dataFim)
            })
          })
        } else if (item.datasHospedagem.length && item.datasBlackout.length) {
          const datasHospedagemNormalizada = this.normalizeDatesArray(
            item.datasHospedagem
          )
          const datasBlackoutNormalizada = this.normalizeDatesArray(
            item.datasBlackout
          )

          periods = this.getNotOverlappedDates(
            this.flattenDates(datasHospedagemNormalizada),
            this.flattenDates(datasBlackoutNormalizada)
          )

          periods = this.extractIntervals(periods)

          if (!periods.length) this.series[0].data.push([index, null, null])
        } else {
          this.series[0].data.push([index, null, null])
        }

        /**
         * Se é uma tarifa prioritária,
         * armazena as datas de hospegem dela para
         * usar como data de blackout de tarifas não prioritárias
         */
        if (item.prioritaria) {
          this.priorityHostingDatesRates = [
            ...this.priorityHostingDatesRates,
            ...periods
          ]
        }

        /** Adiciona os períodos de hospedagem ao gráfico */
        periods.forEach((period) => {
          const colorIndex = index % randomColors.length

          this.series[0].data.push({
            x: index,
            low: period.dataInicio.getTime(),
            high: (
              period.dataFim ?? add(new Date(period.dataInicio), { days: 1 })
            ).getTime(),
            color: randomColors[colorIndex]
          })

          /** Atualize as datas mínimas e máximas */
          minDate = Math.min(minDate, period.dataInicio)
          maxDate = Math.max(maxDate, period.dataFim)
        })
      })

      /** Configure o eixo yAxis para abranger todas as datas */
      this.yAxis[0].min = minDate
      this.yAxis[0].max = maxDate

      this.customHeight = this.xAxis.categories.length * 60

      this.hideChartSection = false
    },

    /**
     * Adiciona todas as datas em um array e remove as duplicadas
     *
     * @param {object[]} datesArr Array de intervalos date-fns
     */
    flattenDates(datesArr) {
      /** Array dos dias dos intervalos */
      const intervalDaysArr = []

      /** extrair os dias dos intervalos e colocar em um array */
      datesArr.forEach((item) => {
        try {
          const datesInterval = this.createDateInterval(item)
          intervalDaysArr.push(...eachDayOfInterval(datesInterval))
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
        }
      })

      /** Retorna um array sem duplicados */
      return intervalDaysArr.filter(
        (item, i, arr) => arr.findIndex((arrItem) => arrItem === item) === i
      )
    },

    /**
     * Cria um objeto no padrão date-fns
     *
     * @param {object} date Data para criar o intervalo no padrão date-fns
     */
    createDateInterval(date) {
      const start =
        date.dataInicio instanceof Date
          ? new Date(date.dataInicio)
          : new Date(this.fixDateLocaleTime(date.dataInicio))

      const end =
        date.dataFim instanceof Date
          ? new Date(date.dataFim)
          : date.dataFim
          ? new Date(this.fixDateLocaleTime(date.dataFim))
          : new Date(
              this.fixDateLocaleTime(
                add(date.dataInicio, { days: 1 }).toISOString()
              )
            )

      return {
        start,
        end
      }
    },

    /**
     * Unifica os períodos que se sobrepoem para reduzir a quantidade
     * de datas no array
     * @param {Array} dateArray: array com as datas
     */
    normalizeDatesArray(dateArray) {
      if (dateArray.length < 2) {
        dateArray = dateArray.map((item) => {
          return {
            dataInicio: new Date(this.fixDateLocaleTime(item.dataInicio)),
            dataFim: new Date(this.fixDateLocaleTime(item.dataFim))
          }
        })

        return dateArray
      }

      /** Armazena as datas já normalizadas */
      const normalizedDatesArray = []

      /** Controlador do loop */
      let runIteration = true

      /** Índice corrente do array de datas  */
      let datesArrayIndex = 0

      while (runIteration) {
        /** Se ainda não existem datas normalizadas */
        if (datesArrayIndex === 0) {
          const data1 = this.createDateInterval(dateArray[0])
          const data2 = this.createDateInterval(dateArray[1])

          if (areIntervalsOverlapping(data1, data2, { inclusive: true })) {
            /**
             * Se as datas se sobrepoem, pega a menor data e a maior,
             * cria uma data normalizada e adiciona ao array
             */
            const dataInicio = min([data1.start, data2.start])
            const dataFim = max([data1.end, data2.end])

            normalizedDatesArray.push({ dataInicio, dataFim })
          } else {
            /**
             * Se as datas não se sobrepoem,
             * adiciona ambas ao array normalizado
             */
            normalizedDatesArray.push({
              dataInicio: new Date(data1.start),
              dataFim: new Date(data1.end)
            })
            normalizedDatesArray.push({
              dataInicio: new Date(data2.start),
              dataFim: new Date(data2.end)
            })
          }

          datesArrayIndex = 2

          if (dateArray.length === 2) {
            runIteration = false
            break
          }

          continue
        }

        /** Armazena o índice do último elemento do array de datas normalizadas */
        const dataHospNormLastIndex = normalizedDatesArray.length - 1

        /** Intervalo de data do último elemento do array de datas normalizadas */
        const data1 = this.createDateInterval(
          normalizedDatesArray[dataHospNormLastIndex]
        )

        /** Intervalo de data do array de datas no índice corrente */
        const data2 = this.createDateInterval(dateArray[datesArrayIndex])
        try {
          if (areIntervalsOverlapping(data1, data2, { inclusive: true })) {
            const dataInicio = min([data1.start, data2.start])
            const dataFim = max([data1.end, data2.end])

            normalizedDatesArray[dataHospNormLastIndex] = {
              dataInicio: new Date(dataInicio),
              dataFim: new Date(dataFim)
            }
          } else {
            normalizedDatesArray.push({
              dataInicio: new Date(data2.start),
              dataFim: new Date(data2.end)
            })
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
        }

        datesArrayIndex++

        if (datesArrayIndex > dateArray.length - 1) runIteration = false
      }

      return normalizedDatesArray
    },

    /**
     * Pega datas que não sobrepoem
     * @param {array} arr1 hospedagem
     * @param {array} arr2 blackout
     */

    getNotOverlappedDates(arr1, arr2) {
      return arr1.filter(
        (item1) => !arr2.find((item2) => item2.getTime() === item1.getTime())
      )
    },

    /**
     * Monta os períodos de hospedagem que não são afetados pelas datas de blackout.
     *
     * @param {string[]} arr Array com os valores de todos os dias contidos nos intervalos de data.
     */
    extractIntervals(arr) {
      /** Array com os intervalos já salvos */
      const intervalsArray = []
      /** Indice do último item do array de intervalos */
      let intervalsArrayIndex = 0
      /** Objeto de interalo */
      let intervalObj = { dataInicio: null, dataFim: null }

      for (let i = 0, len = arr.length; i < len; i++) {
        const date = arr[i]

        /**
         * Se não tem dataInicio no obj, é um objeto novo
         * e adiciona a data que está sendo iterada à ele
         */
        if (!intervalObj.dataInicio) {
          intervalObj.dataInicio = new Date(date)
        }

        /**
         * Se o array de intervalos estiver vazio
         * adiciona o obj de intervalo nele e reinicia o loop
         * para pegar a próxima data
         */
        if (!intervalsArray.length) {
          intervalsArray.push(intervalObj)
          continue
        }

        /**
         * Se o obj tem dataFim usa o valor para comparar
         * com a data atual da iteração senão usa a dataInicio
         */
        const valueToCompare =
          intervalsArray[intervalsArrayIndex].dataFim ??
          intervalsArray[intervalsArrayIndex].dataInicio

        /**
         * Armazena a diferença de dias entre a data atual
         * data iteração com a data de comparação
         */
        const dayDifference = Math.abs(
          differenceInDays(new Date(date), new Date(valueToCompare))
        )

        /**
         * Se a diferença é igual a um, está
         * no mesmo intervalo de dias e adiciona a
         * data atual da iteração ao atributo dataFim do obj
         */
        if (dayDifference === 1) {
          intervalsArray[intervalsArrayIndex].dataFim = new Date(date)
        } else if (dayDifference > 1) {
          /**
           * Se a diferença é maior que 1,
           * reinicializa o obj de intervalo para criar
           * um novo período
           */
          intervalObj = { dataInicio: new Date(date), dataFim: null }
          intervalsArray.push(intervalObj)
          intervalsArrayIndex++
        }
      }

      return intervalsArray
    },

    /** Função que gera uma cor hexadecimal aleatória que não esteja na lista de cores usadas.
     * @param { Array } usedColors - Array de cores já usadas para não repetir.
     */
    getRandomColor(usedColors) {
      const letters = '0123456789ABCDEF'
      let color
      do {
        color = '#'
        for (let i = 0; i < 6; i++) {
          color += letters[Math.floor(Math.random() * 16)]
        }
      } while (usedColors.includes(color))
      return color
    },

    /** Função que cria um array de cores aleatórias, garantindo que sejam todas diferentes.
     * @param { Number } length - Quantidade de cores a serem criadas
     */
    createRandomColorArray(length) {
      const colors = []
      for (let i = 0; i < length; i++) {
        const randomColor = this.getRandomColor(colors)
        colors.push(randomColor)
      }
      return colors
    },

    /**
     * Corrige o timezone do iso
     * para manter a coerência das datas
     *
     * @param {string} date Data no formato ISO
     */
    fixDateLocaleTime(date) {
      if (!date) return

      const isoDate = date instanceof Date ? date.toISOString() : date

      return new Date(`${isoDate.replace(/T.*/g, 'T03:00:00')}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.active-flutctuation {
  .graph {
    margin-top: 10px;
  }

  &--title {
    margin: 30px 0 0 10px;
    color: pmw-colors('grey-90');

    &--tooltip {
      line-height: 18px;
    }
  }
}
</style>
